import type {
  Application,
  ApplicationWithDetails,
  DetailedResumeStatus,
  Job,
  JobListResponse,
  ParsedResumeData,
  Resume,
  ResumeWithCandidate,
} from "./types";

const toIsoString = (value: unknown): string | undefined => {
  if (!value) return undefined;
  if (value instanceof Date) return value.toISOString();
  if (typeof value === "string") return value;
  return undefined;
};

const toBoolean = (value: unknown): boolean => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string")
    return value === "1" || value.toLowerCase() === "true";
  return false;
};

const toSalaryNumber = (value: unknown): number | undefined => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
};

const formatSalary = (
  salaryMin?: number | null,
  salaryMax?: number | null,
): string | undefined => {
  const min = toSalaryNumber(salaryMin);
  const max = toSalaryNumber(salaryMax);

  if (min !== undefined && max !== undefined) {
    return `INR ${min.toLocaleString()} - ${max.toLocaleString()}`;
  }
  if (min !== undefined) return `From INR ${min.toLocaleString()}`;
  if (max !== undefined) return `Up to INR ${max.toLocaleString()}`;
  return undefined;
};

export const normalizeJob = (job: Job): Job => {
  const createdAt = toIsoString(job.created_at);
  const updatedAt = toIsoString(job.updated_at);
  const publishedAt = toIsoString(job.published_at);
  const featured = toBoolean(job.is_featured ?? job.featured);

  return {
    ...job,
    created_at: createdAt,
    updated_at: updatedAt,
    published_at: publishedAt,
    is_featured: featured,
    featured,
    postedDate:
      job.postedDate ?? publishedAt ?? createdAt ?? new Date().toISOString(),
    type: job.type ?? job.job_type,
    experience: job.experience ?? job.experience_level,
    companyLogo: job.companyLogo ?? job.company_logo_url,
    salary: job.salary ?? formatSalary(job.salary_min, job.salary_max),
  };
};

export const normalizeJobListResponse = (
  response: JobListResponse,
): JobListResponse => {
  const page = response.page ?? 1;
  const pageSize = response.page_size ?? response.jobs.length;
  const totalPages = pageSize > 0 ? Math.ceil(response.total / pageSize) : 1;

  return {
    ...response,
    jobs: response.jobs.map(normalizeJob),
    page,
    page_size: pageSize,
    totalPages: response.totalPages ?? totalPages,
    hasMore: response.hasMore ?? page < totalPages,
  };
};

export const normalizeApplication = <
  T extends Application | ApplicationWithDetails,
>(
  application: T,
): T => {
  const appliedAt = toIsoString(
    application.applied_at ?? application.appliedAt,
  );
  const updatedAt = toIsoString(
    application.updated_at ?? application.updatedAt,
  );
  const details = application as ApplicationWithDetails;

  return {
    ...application,
    applied_at: appliedAt,
    updated_at: updatedAt,
    userId: application.userId ?? application.user_id,
    jobId: application.jobId ?? application.job_id,
    resumeId: application.resumeId ?? application.resume_id,
    coverLetter: application.coverLetter ?? application.cover_letter,
    appliedAt,
    updatedAt,
    jobTitle: details.jobTitle ?? details.job_title,
    jobCompany: details.jobCompany ?? details.job_company,
    jobLocation: details.jobLocation ?? details.job_location,
    resumeFilename: details.resumeFilename ?? details.resume_filename,
    resumeLabel: details.resumeLabel ?? details.resume_label,
    statusHistory: details.statusHistory ?? details.status_history,
  };
};

const parseJsonObject = (
  value: unknown,
): Record<string, unknown> | undefined => {
  if (!value) return undefined;
  if (typeof value === "object") return value as Record<string, unknown>;
  if (typeof value !== "string") return undefined;

  try {
    const parsed = JSON.parse(value);
    return typeof parsed === "object" && parsed !== null ? parsed : undefined;
  } catch {
    return undefined;
  }
};

const asArray = <T>(value: unknown): T[] =>
  Array.isArray(value) ? (value as T[]) : [];

const normalizeParsedResumeData = (
  parsedJson?: string,
): ParsedResumeData | undefined => {
  const parsed = parseJsonObject(parsedJson);
  if (!parsed) return undefined;

  const extracted = parseJsonObject(parsed.extracted_data) ?? parsed;

  return {
    name: extracted.name as string | undefined,
    email: extracted.email as string | undefined,
    phone: extracted.phone as string | undefined,
    skills: asArray<string>(extracted.skills),
    experience: asArray<Record<string, unknown>>(extracted.experience).map(
      (experience) => ({
        company: experience.company as string | undefined,
        title: experience.title as string | undefined,
        startDate: (experience.start_date ?? experience.start) as
          | string
          | undefined,
        endDate: (experience.end_date ?? experience.end) as string | undefined,
        description: (experience.description as string | undefined) ?? "",
        location: experience.location as string | undefined,
      }),
    ),
    education: asArray<Record<string, unknown>>(extracted.education).map(
      (education) => ({
        institution: education.institution as string | undefined,
        degree: education.degree as string | undefined,
        field: (education.field_of_study ?? education.field) as
          | string
          | undefined,
        startDate: (education.start_date ?? education.start) as
          | string
          | undefined,
        endDate: (education.end_date ?? education.end) as string | undefined,
        gpa: education.gpa as string | undefined,
      }),
    ),
    summary: extracted.summary as string | undefined,
    strengths: asArray<string>(extracted.strengths),
    areas_for_improvement: asArray<string>(extracted.areas_for_improvement),
    score_breakdown:
      extracted.score_breakdown as ParsedResumeData["score_breakdown"],
  };
};

const originalFilename = (filename: string): string => {
  const underscoreIndex = filename.indexOf("_");
  return underscoreIndex > 0
    ? filename.substring(underscoreIndex + 1)
    : filename;
};

export type NormalizedResume<T extends Resume = Resume> = T & {
  userId: string;
  originalFilename: string;
  submittedAt: string;
  fileSize: number;
  fileType: string;
};
export const normalizeResume = <T extends Resume | ResumeWithCandidate>(
  resume: T,
): NormalizedResume<T> => {
  const submittedAt = toIsoString(resume.submitted_at ?? resume.submittedAt);
  const processedAt = toIsoString(
    (resume as Resume).processed_at ?? resume.processedAt,
  );

  return {
    ...resume,
    submitted_at: submittedAt,
    processed_at: processedAt,
    userId: resume.userId ?? resume.user_id,
    originalFilename:
      resume.originalFilename ?? originalFilename(resume.filename ?? ""),
    fileSize: resume.fileSize ?? resume.file_size ?? 0,
    submittedAt: submittedAt ?? new Date().toISOString(),
    processedAt,
    fileType: resume.fileType ?? "application/pdf",
    parsedData:
      resume.parsedData ?? normalizeParsedResumeData(resume.parsed_json),
  } as NormalizedResume<T>;
};

export const normalizeResumeStatus = (
  status: DetailedResumeStatus,
): DetailedResumeStatus => ({
  ...status,
  id: status.id ?? status.resume_id,
  progress: status.progress ?? status.progress_percentage,
  stage: status.stage ?? status.current_step,
  errorMessage: status.errorMessage ?? status.error_messages?.join(", "),
});
